import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { resolveImageUrl } from "../../utils/imageUrl";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Plus, Trash2, Copy, Calendar, Loader2, Upload,
  Sparkles, PartyPopper, ImageIcon, X, Pencil, Save, Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITION_OPTIONS = [
  { value: "top-banner", label: "Top Banner" },
  { value: "corner-top-left", label: "Corner: Top Left" },
  { value: "corner-top-right", label: "Corner: Top Right" },
  { value: "corner-bottom-left", label: "Corner: Bottom Left" },
  { value: "corner-bottom-right", label: "Corner: Bottom Right" },
  { value: "floating-left", label: "Floating Left" },
  { value: "floating-right", label: "Floating Right" },
];

const HOLIDAY_PRESETS = [
  { id: "christmas", name: "Christmas", color: "#c41e3a", emoji: "\u{1F384}", month: 12, day: 25, duration: 14, image: "https://images.unsplash.com/photo-1512484346026-44def92f7385?w=400&q=80" },
  { id: "new-year", name: "New Year", color: "#ffd700", emoji: "\u{1F386}", month: 1, day: 1, duration: 7, image: "https://images.unsplash.com/photo-1704399527621-82de0422490c?w=400&q=80" },
  { id: "easter", name: "Easter", color: "#c8a2c8", emoji: "\u{1F423}", month: 4, day: 20, duration: 7, image: "https://images.unsplash.com/photo-1770732418316-fbe6a816120a?w=400&q=80" },
  { id: "valentine", name: "Valentine's Day", color: "#e91e63", emoji: "\u{2764}\u{FE0F}", month: 2, day: 14, duration: 5, image: "https://images.pexels.com/photos/6371/red-love-heart-valentines.jpg?auto=compress&w=400" },
  { id: "independence", name: "Ghana Independence Day", color: "#006b3f", emoji: "\u{1F1EC}\u{1F1ED}", month: 3, day: 6, duration: 3, image: "https://images.unsplash.com/photo-1742476126735-cba186771cf2?w=400&q=80" },
  { id: "constitution", name: "Constitution Day", color: "#ce1126", emoji: "\u{1F4DC}", month: 1, day: 7, duration: 2 },
  { id: "may-day", name: "May Day", color: "#ff6b35", emoji: "\u{1F528}", month: 5, day: 1, duration: 2 },
  { id: "au-day", name: "African Union Day", color: "#009639", emoji: "\u{1F30D}", month: 5, day: 25, duration: 2 },
  { id: "republic", name: "Republic Day", color: "#fcd116", emoji: "\u{1F3DB}\u{FE0F}", month: 7, day: 1, duration: 2 },
  { id: "founders", name: "Founders' Day", color: "#006b3f", emoji: "\u{2B50}", month: 8, day: 4, duration: 2 },
  { id: "farmers", name: "Farmers' Day", color: "#228b22", emoji: "\u{1F33E}", month: 12, day: 6, duration: 2 },
  { id: "eid", name: "Eid Celebration", color: "#1a8751", emoji: "\u{1F319}", month: 0, day: 0, duration: 3 },
];

export function DecorationsManager({ darkMode, viewOnly }) {
  const [decorations, setDecorations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [filter, setFilter] = useState("all");

  const token = localStorage.getItem("gmp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDecorations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/decorations`, { headers });
      setDecorations(res.data);
    } catch (err) {
      toast.error("Failed to load decorations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDecorations(); }, [fetchDecorations]);

  const createFromPreset = async (preset) => {
    const year = new Date().getFullYear();
    const startDate = preset.month > 0
      ? new Date(year, preset.month - 1, Math.max(1, preset.day - Math.floor(preset.duration / 2)))
      : null;
    const endDate = startDate
      ? new Date(startDate.getTime() + preset.duration * 86400000)
      : null;

    const data = {
      name: `${preset.name} ${year}`,
      holiday: preset.id,
      decoration_type: "banner",
      image_url: preset.image || null,
      position: "top-banner",
      styles: {
        background_color: preset.color,
        text_color: "#ffffff",
        text: `Happy ${preset.name}!`,
        opacity: 1,
        height: "60px",
      },
      schedule_start: startDate?.toISOString() || null,
      schedule_end: endDate?.toISOString() || null,
      is_active: false,
    };
    try {
      const res = await axios.post(`${API}/admin/decorations`, data, { headers });
      setDecorations(prev => [res.data, ...prev]);
      setShowPresets(false);
      toast.success(`${preset.name} decoration created`);
    } catch (err) {
      toast.error("Failed to create decoration");
    }
  };

  const createBlank = async () => {
    const data = {
      name: "New Decoration",
      decoration_type: "banner",
      position: "top-banner",
      styles: { background_color: "#1a1a2e", text_color: "#ffffff", text: "Special Announcement!", opacity: 1, height: "60px" },
      is_active: false,
    };
    try {
      const res = await axios.post(`${API}/admin/decorations`, data, { headers });
      setDecorations(prev => [res.data, ...prev]);
      setEditing(res.data.id);
      toast.success("Decoration created");
    } catch (err) {
      toast.error("Failed to create decoration");
    }
  };

  const updateDecoration = async (id, updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/decorations/${id}`, updates, { headers });
      setDecorations(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
      toast.success("Decoration updated");
    } catch (err) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const deleteDecoration = async (id) => {
    try {
      await axios.delete(`${API}/admin/decorations/${id}`, { headers });
      setDecorations(prev => prev.filter(d => d.id !== id));
      if (editing === id) setEditing(null);
      toast.success("Decoration deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const duplicateDecoration = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/decorations/${id}/duplicate`, {}, { headers });
      setDecorations(prev => [res.data, ...prev]);
      toast.success("Decoration duplicated");
    } catch (err) {
      toast.error("Failed to duplicate");
    }
  };

  const toggleActive = async (id, currentState) => {
    await updateDecoration(id, { is_active: !currentState });
  };

  const saveAsTemplate = async (id) => {
    await updateDecoration(id, { is_template: true });
  };

  const handleImageUpload = async (id, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/decorations/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      await updateDecoration(id, { image_url: res.data.url });
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const dm = darkMode;
  const filtered = decorations.filter(d => {
    if (filter === "active") return d.is_active;
    if (filter === "templates") return d.is_template;
    if (filter === "scheduled") return d.schedule_start && d.schedule_end;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className={`w-6 h-6 animate-spin ${dm ? "text-gray-400" : "text-gray-500"}`} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="decorations-manager">
      {/* Header actions */}
      {!viewOnly && (
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setShowPresets(!showPresets)} className="rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white hover:from-pink-600 hover:to-orange-600" data-testid="show-presets-btn">
          <Sparkles className="w-4 h-4 mr-2" /> Holiday Presets
        </Button>
        <Button onClick={createBlank} variant="outline" className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="create-blank-btn">
          <Plus className="w-4 h-4 mr-2" /> Custom Decoration
        </Button>
      </div>
      )}

      {/* Preset Library */}
      {showPresets && (
        <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-700/50 border-pink-700/30" : "bg-pink-50/50 border-pink-200"}`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-base ${dm ? "text-gray-100" : ""}`}>Holiday Preset Library</CardTitle>
            <CardDescription className={dm ? "text-gray-400" : ""}>
              Click a holiday to create a ready-made decoration with suggested dates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {HOLIDAY_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => createFromPreset(preset)}
                  className={`p-3 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${dm ? "bg-gray-600/50 border-gray-500 hover:border-gray-400" : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"}`}
                  data-testid={`preset-${preset.id}`}
                >
                  <span className="text-2xl">{preset.emoji}</span>
                  <p className={`text-sm font-medium mt-1 ${dm ? "text-gray-200" : "text-gray-700"}`}>{preset.name}</p>
                  {preset.month > 0 && (
                    <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                      {new Date(2026, preset.month - 1, preset.day).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </p>
                  )}
                  <div className="w-full h-1.5 rounded-full mt-2" style={{ backgroundColor: preset.color }} />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "scheduled", label: "Scheduled" },
          { key: "templates", label: "Templates" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              filter === f.key
                ? "bg-indigo-600 text-white"
                : (dm ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200")
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <span className={`text-xs self-center ml-2 ${dm ? "text-gray-400" : "text-gray-500"}`}>
          {filtered.length} decoration{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Decorations list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <PartyPopper className={`w-12 h-12 mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
          <h3 className={`font-semibold mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>No decorations yet</h3>
          <p className={`text-sm ${dm ? "text-gray-500" : "text-gray-400"}`}>
            Use the Holiday Presets above to get started quickly!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((dec) => (
            <DecorationCard
              key={dec.id}
              decoration={dec}
              isEditing={editing === dec.id}
              onEdit={() => setEditing(editing === dec.id ? null : dec.id)}
              onUpdate={(updates) => updateDecoration(dec.id, updates)}
              onDelete={() => deleteDecoration(dec.id)}
              onDuplicate={() => duplicateDecoration(dec.id)}
              onToggleActive={() => toggleActive(dec.id, dec.is_active)}
              onSaveTemplate={() => saveAsTemplate(dec.id)}
              onImageUpload={(file) => handleImageUpload(dec.id, file)}
              uploading={uploading}
              saving={saving}
              darkMode={dm}
              viewOnly={viewOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DecorationCard({
  decoration: dec, isEditing, onEdit, onUpdate, onDelete, onDuplicate,
  onToggleActive, onSaveTemplate, onImageUpload, uploading, saving, darkMode: dm, viewOnly
}) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (isEditing) {
      setForm({
        name: dec.name || "",
        decoration_type: dec.decoration_type || "banner",
        position: dec.position || "top-banner",
        schedule_start: dec.schedule_start ? dec.schedule_start.slice(0, 16) : "",
        schedule_end: dec.schedule_end ? dec.schedule_end.slice(0, 16) : "",
        text: dec.styles?.text || "",
        background_color: dec.styles?.background_color || "#1a1a2e",
        text_color: dec.styles?.text_color || "#ffffff",
        opacity: dec.styles?.opacity ?? 1,
        height: dec.styles?.height || "60px",
      });
    }
  }, [isEditing, dec]);

  const handleSave = () => {
    onUpdate({
      name: form.name,
      decoration_type: form.decoration_type,
      position: form.position,
      schedule_start: form.schedule_start ? new Date(form.schedule_start).toISOString() : null,
      schedule_end: form.schedule_end ? new Date(form.schedule_end).toISOString() : null,
      styles: {
        text: form.text,
        background_color: form.background_color,
        text_color: form.text_color,
        opacity: parseFloat(form.opacity) || 1,
        height: form.height,
      },
    });
    onEdit();
  };

  const isScheduled = dec.schedule_start && dec.schedule_end;
  const now = new Date().toISOString();
  const isWithinSchedule = isScheduled && dec.schedule_start <= now && now <= dec.schedule_end;
  const presetInfo = HOLIDAY_PRESETS.find(p => p.id === dec.holiday);

  return (
    <div
      className={`rounded-xl border-2 overflow-hidden transition-all ${
        dec.is_active
          ? (dm ? "border-green-700/50 bg-gray-800/80" : "border-green-300 bg-green-50/30")
          : (dm ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white")
      }`}
      data-testid={`decoration-${dec.id}`}
    >
      {/* Preview bar */}
      {dec.styles?.text && (
        <div
          className="flex items-center justify-center text-sm font-medium"
          style={{
            backgroundColor: dec.styles?.background_color || "#1a1a2e",
            color: dec.styles?.text_color || "#fff",
            height: dec.styles?.height || "60px",
            opacity: dec.styles?.opacity ?? 1,
            backgroundImage: dec.image_url ? `url(${resolveImageUrl(dec.image_url)})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid={`decoration-preview-${dec.id}`}
        >
          <span className="px-4 py-1 rounded-md" style={{
            backgroundColor: dec.image_url ? `${dec.styles?.background_color || "#1a1a2e"}cc` : "transparent"
          }}>
            {presetInfo && <span className="mr-2">{presetInfo.emoji}</span>}
            {dec.styles.text}
          </span>
        </div>
      )}

      {/* Info row */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div>
              <div className="flex items-center gap-2">
                <p className={`font-medium text-sm truncate ${dm ? "text-gray-200" : "text-gray-700"}`}>{dec.name}</p>
                {dec.is_template && <Badge className="bg-purple-500/20 text-purple-600 text-xs">Template</Badge>}
                {dec.is_active && <Badge className="bg-green-500/20 text-green-600 text-xs">Active</Badge>}
                {isScheduled && !isWithinSchedule && <Badge className="bg-blue-500/20 text-blue-600 text-xs">Scheduled</Badge>}
              </div>
              <div className={`flex items-center gap-3 text-xs mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                <span>{dec.position?.replace("-", " ")}</span>
                {isScheduled && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(dec.schedule_start).toLocaleDateString()} - {new Date(dec.schedule_end).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!viewOnly && (
              <>
                <Switch
                  checked={dec.is_active}
                  onCheckedChange={onToggleActive}
                  data-testid={`toggle-decoration-${dec.id}`}
                />
                <Button variant="ghost" size="sm" onClick={onEdit} className={`h-8 w-8 p-0 ${dm ? "text-gray-400 hover:text-gray-200" : ""}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={onDuplicate} className={`h-8 w-8 p-0 ${dm ? "text-gray-400 hover:text-gray-200" : ""}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={onDelete} className="h-8 w-8 p-0 text-red-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Edit form */}
        {isEditing && !viewOnly && (
          <div className={`mt-4 pt-4 border-t space-y-4 ${dm ? "border-gray-700" : "border-gray-200"}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Name</label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  className={`rounded-lg text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid={`edit-name-${dec.id}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Position</label>
                <select value={form.position} onChange={e => setForm(f => ({...f, position: e.target.value}))}
                  className={`w-full h-9 rounded-lg border text-sm px-3 ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : "bg-white border-gray-300"}`}>
                  {POSITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Banner Text</label>
              <Input value={form.text} onChange={e => setForm(f => ({...f, text: e.target.value}))}
                className={`rounded-lg text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid={`edit-text-${dec.id}`} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.background_color} onChange={e => setForm(f => ({...f, background_color: e.target.value}))}
                    className="w-8 h-8 rounded cursor-pointer border" />
                  <span className={`text-xs font-mono ${dm ? "text-gray-400" : "text-gray-500"}`}>{form.background_color}</span>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.text_color} onChange={e => setForm(f => ({...f, text_color: e.target.value}))}
                    className="w-8 h-8 rounded cursor-pointer border" />
                  <span className={`text-xs font-mono ${dm ? "text-gray-400" : "text-gray-500"}`}>{form.text_color}</span>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Height</label>
                <Input value={form.height} onChange={e => setForm(f => ({...f, height: e.target.value}))}
                  className={`rounded-lg text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} placeholder="60px" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Schedule Start</label>
                <Input type="datetime-local" value={form.schedule_start} onChange={e => setForm(f => ({...f, schedule_start: e.target.value}))}
                  className={`rounded-lg text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid={`schedule-start-${dec.id}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Schedule End</label>
                <Input type="datetime-local" value={form.schedule_end} onChange={e => setForm(f => ({...f, schedule_end: e.target.value}))}
                  className={`rounded-lg text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid={`schedule-end-${dec.id}`} />
              </div>
            </div>
            {/* Image upload */}
            <div>
              <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Background Image</label>
              <div className="flex items-center gap-3">
                {dec.image_url && (
                  <img src={resolveImageUrl(dec.image_url)} alt="" className="w-16 h-10 object-cover rounded" />
                )}
                <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${
                  dm ? "bg-gray-600 text-gray-300 hover:bg-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {uploading ? "Uploading..." : "Upload"}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={e => { if (e.target.files?.[0]) onImageUpload(e.target.files[0]); e.target.value = ""; }}
                    disabled={uploading} />
                </label>
                {dec.image_url && (
                  <Button variant="ghost" size="sm" onClick={() => onUpdate({ image_url: null })} className="h-8 text-xs text-red-500">
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => onSaveTemplate()}
                className={`rounded-lg text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-300" : ""}`}>
                <Save className="w-3 h-3 mr-1" /> Save as Template
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onEdit} className={`rounded-lg ${dm ? "bg-gray-700 border-gray-600 text-gray-300" : ""}`}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-lg bg-green-600 hover:bg-green-700 text-white" data-testid={`save-decoration-${dec.id}`}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
